'use client';

import { Check, ChevronDown, ChevronUp, History, RotateCcw, Sparkles, X } from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';

import { cn } from '../../lib/cn';

type IconType = React.ComponentType<{ className?: string }>;

export interface AiRecItem {
	/** Id univoco della raccomandazione. */
	id: string;
	/** Titolo della raccomandazione. */
	label: string;
	/** Descrizione estesa. */
	desc: string;
	/** Data/ora ISO a cui si riferisce la raccomandazione; le voci passate mostrano la UI di risposta Sì/No. */
	datetime: string;
	/** Categoria di appartenenza (chiave di `categoryLabels`). */
	category: string;
	/** Icona della raccomandazione. */
	icon: IconType;
}

export interface AiRecCategoryMeta {
	/** Etichetta della categoria mostrata nel tab. */
	label: string;
	/** Icona del tab di categoria. */
	icon: IconType;
}

export interface AiRecommendationsPanelViewProps<TRec extends AiRecItem> {
	/** Tutte le raccomandazioni (di tutte le categorie). */
	recs: TRec[];
	/** Chiavi di categoria mostrate come tab, nell'ordine desiderato. */
	categories: string[];
	/** Etichetta e icona per ogni categoria. */
	categoryLabels: Record<string, AiRecCategoryMeta>;
	/** Risposta per id raccomandazione: `'no'`, oppure la data ISO in cui è stata implementata; assente = non ancora risposta. */
	applied: Record<string, string | undefined>;
	/** Formatta una data/ora ISO per la visualizzazione. */
	formatDateTime: (iso: string) => string;
	/** Chiamata alla risposta Sì/No a una raccomandazione passata. */
	onFeedback: (id: string, answer: 'si' | 'no') => void;
	/** Chiamata all'annullamento di una risposta già data (torna tra le raccomandazioni in sospeso). */
	onClearFeedback: (id: string) => void;
	/** Se presente, mostra un bottone "Spiega con l'AI Coach" per le raccomandazioni future e viene chiamata al click. */
	onExplain?: (rec: TRec) => void;
	/** Testo del bottone di spiegazione. Default: "Spiega con l'AI Coach". */
	explainLabel?: string;
}

/**
 * Card delle raccomandazioni generate dall'AI per ottimizzare produzione/consumo della CER:
 * tab per categoria, elenco delle raccomandazioni in sospeso con feedback Sì/No, e uno
 * storico consultabile delle risposte già date.
 */
function AiRecommendationsPanelView<TRec extends AiRecItem>({
	recs,
	categories,
	categoryLabels,
	applied,
	formatDateTime,
	onFeedback,
	onClearFeedback,
	onExplain,
	explainLabel = "Spiega con l'AI Coach",
}: AiRecommendationsPanelViewProps<TRec>) {
	const [activeTab, setActiveTab] = useState(categories[0]);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const [showHistory, setShowHistory] = useState(false);

	const now = Date.now();
	const answeredCount = recs.filter((r) => !!applied[r.id]).length;
	const implementedCount = recs.filter((r) => applied[r.id] && applied[r.id] !== 'no').length;
	const answered = useMemo(() => recs.filter((r) => !!applied[r.id]), [recs, applied]);

	const pending = useMemo(
		() => recs.filter((r) => r.category === activeTab && !applied[r.id]),
		[recs, activeTab, applied],
	);

	function toggle(id: string) {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">Raccomandazioni AI</span>
				<span className="card-badge badge-purple">
					{answeredCount}/{recs.length}
				</span>
			</div>

			{/* Category tabs */}
			<div className="mb-2 flex gap-2">
				{categories.map((cat) => {
					const meta = categoryLabels[cat];
					const Icon = meta.icon;
					const count = recs.filter((r) => r.category === cat && !applied[r.id]).length;
					const isActive = activeTab === cat;
					return (
						<button
							key={cat}
							type="button"
							onClick={() => {
								setActiveTab(cat);
								setExpanded(new Set());
							}}
							className={cn(
								'flex flex-1 flex-col items-center gap-1 rounded-lg border px-2 py-2 transition-colors',
								isActive
									? 'border-accent-purple/60 bg-accent-purple/[0.08]'
									: 'border-border hover:border-accent-purple/40',
							)}
						>
							<Icon
								className={cn('h-3.5 w-3.5', isActive ? 'text-accent-purple' : 'text-text-muted')}
							/>
							<span
								className={cn(
									'text-center text-[12px] font-bold leading-tight',
									isActive ? 'text-accent-purple' : 'text-text-muted',
								)}
							>
								{meta.label}
							</span>
							<span
								className={cn(
									'rounded-full px-1.5 text-[12px] font-extrabold',
									isActive ? 'bg-accent-purple/20 text-accent-purple' : 'bg-border text-text-muted',
								)}
							>
								{count}
							</span>
						</button>
					);
				})}
			</div>

			{/* Pending recommendations */}
			{pending.length === 0 ? (
				<div className="flex flex-col items-center gap-2 py-5 text-center">
					<Check className="h-6 w-6 text-accent-green" />
					<p className="text-[14px] font-semibold text-text-primary">
						Hai risposto a tutte le raccomandazioni
					</p>
					<p className="text-[12px] text-text-muted">
						Ottimo lavoro. Torna qui per i prossimi aggiornamenti.
					</p>
				</div>
			) : (
				<div className="space-y-2">
					{pending.map((rec) => {
						const Icon = rec.icon;
						const isPast = new Date(rec.datetime).getTime() < now;
						const isExpanded = expanded.has(rec.id);
						return (
							<div key={rec.id} className="rounded-xl border border-border bg-background/40 p-3">
								<div className="mb-2 flex items-center gap-2">
									<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-purple/15">
										<Icon className="h-3.5 w-3.5 text-accent-purple" />
									</span>
									<span className="flex-1 text-[13px] font-bold text-text-primary">
										{rec.label}
									</span>
									<span
										className={cn(
											'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold',
											isPast ? 'bg-border text-text-muted' : 'bg-warn/15 text-warn',
										)}
									>
										{formatDateTime(rec.datetime)}
									</span>
								</div>

								<p
									className={cn(
										'text-[13px] leading-relaxed text-text-muted',
										!isExpanded && 'line-clamp-1',
									)}
								>
									{rec.desc}
								</p>
								<button
									type="button"
									onClick={() => toggle(rec.id)}
									className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[12px] text-text-muted transition-colors hover:text-text-primary"
								>
									{isExpanded ? (
										<ChevronUp className="h-3 w-3" />
									) : (
										<ChevronDown className="h-3 w-3" />
									)}
									{isExpanded ? 'Riduci' : 'Leggi tutto'}
								</button>

								<div className="mt-2.5 flex items-center justify-end gap-2 border-t border-border pt-2.5">
									{isPast ? (
										<>
											<button
												type="button"
												onClick={() => onFeedback(rec.id, 'no')}
												className="rounded-full border border-border px-4 py-1.5 text-[13px] font-semibold text-text-muted transition-colors hover:border-warn/60 hover:text-warn"
											>
												<X className="mr-1 inline h-3 w-3" />
												No
											</button>
											<button
												type="button"
												onClick={() => onFeedback(rec.id, 'si')}
												className="rounded-full bg-accent-green px-4 py-1.5 text-[13px] font-bold text-black transition-opacity hover:opacity-90"
											>
												<Check className="mr-1 inline h-3 w-3" />
												Sì
											</button>
										</>
									) : (
										onExplain && (
											<button
												type="button"
												onClick={() => onExplain(rec)}
												className="inline-flex items-center gap-1 text-[13px] font-semibold text-accent-purple transition-opacity hover:opacity-80"
											>
												<Sparkles className="h-3.5 w-3.5" />
												{explainLabel}
											</button>
										)
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Riepilogo raccomandazioni — storico risposte (parità con app mobile) */}
			<button
				type="button"
				onClick={() => setShowHistory((v) => !v)}
				className="mt-2 flex w-full items-center justify-between rounded-xl border border-accent-purple/40 bg-background/40 px-3 py-2.5 transition-colors hover:border-accent-purple/60"
			>
				<span className="flex items-center gap-1.5 text-[13px] font-bold text-accent-purple">
					<History className="h-3.5 w-3.5" />
					Riepilogo raccomandazioni
				</span>
				<span className="flex items-center gap-1.5">
					<span
						className={cn(
							'rounded-full px-2 py-0.5 text-[12px] font-extrabold',
							implementedCount > 0
								? 'bg-accent-green/20 text-accent-green'
								: 'bg-border text-text-muted',
						)}
					>
						{answeredCount}/{recs.length}
					</span>
					{showHistory ? (
						<ChevronUp className="h-4 w-4 text-text-muted" />
					) : (
						<ChevronDown className="h-4 w-4 text-text-muted" />
					)}
				</span>
			</button>

			{showHistory && (
				<div className="mt-2 space-y-1.5">
					{answered.length === 0 ? (
						<p className="py-3 text-center text-[12px] text-text-muted">
							Nessuna risposta ancora. Rispondi Sì o No alle raccomandazioni per costruire lo
							storico.
						</p>
					) : (
						answered.map((rec) => {
							const Icon = rec.icon;
							const value = applied[rec.id];
							const implemented = value !== 'no';
							const when =
								implemented && value
									? new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
									: null;
							return (
								<div
									key={rec.id}
									className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2"
								>
									<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-purple/15">
										<Icon className="h-3 w-3 text-accent-purple" />
									</span>
									<span className="min-w-0 flex-1 truncate text-[12px] text-text-primary">
										{rec.label}
									</span>
									<span
										className={cn(
											'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold',
											implemented
												? 'bg-accent-green/15 text-accent-green'
												: 'bg-border text-text-muted',
										)}
									>
										{implemented ? `Implementata${when ? ` · ${when}` : ''}` : 'Ignorata'}
									</span>
									<button
										type="button"
										onClick={() => onClearFeedback(rec.id)}
										aria-label="Annulla risposta"
										title="Annulla risposta"
										className="shrink-0 text-text-muted transition-colors hover:text-text-primary"
									>
										<RotateCcw className="h-3 w-3" />
									</button>
								</div>
							);
						})
					)}
				</div>
			)}

			<p className="mt-2 text-[11px] text-text-muted">
				In base ai dati energetici della CER suggerisco azioni per ottimizzare produzione e consumo.
				Rispondi Sì o No per tenere traccia di cosa hai già implementato.
			</p>
		</div>
	);
}
AiRecommendationsPanelView.displayName = 'AiRecommendationsPanelView';

export { AiRecommendationsPanelView };
